/**
 * ## Ours (Base UI)
 * Single file serves as both ContextMenu and DropdownMenu — every export is duplicated under both `Menu*` and `DropdownMenu*` names.
 * `MenuCheckboxItem` has `variant='switch'` which renders a `Switch` instead of a checkmark.
 * `MenuItem` variant is `'error'` (vs Shadcn's `'destructive'`). `MenuShortcut` is a `<kbd>`.
 * * `Menu` / `DropdownMenu` — root
 * * `MenuPortal` / `DropdownMenuPortal` — portal
 * * `MenuTrigger` / `DropdownMenuTrigger` — trigger
 * * `MenuPopup` / `DropdownMenuContent` — collapses Positioner + Popup; `side`, `align`, `sideOffset`, `alignOffset`
 * * `MenuGroup` / `DropdownMenuGroup` — group
 * * `MenuItem` / `DropdownMenuItem` — item; `inset?: boolean`, `variant?: 'default'|'error'`
 * * `MenuCheckboxItem` / `DropdownMenuCheckboxItem` — `variant?: 'default'|'switch'`
 * * `MenuRadioGroup` / `DropdownMenuRadioGroup` — radio group
 * * `MenuRadioItem` / `DropdownMenuRadioItem` — radio item
 * * `MenuGroupLabel` / `DropdownMenuLabel` — label; `inset?: boolean`
 * * `MenuSeparator` / `DropdownMenuSeparator` — separator
 * * `MenuShortcut` / `DropdownMenuShortcut` — `<kbd>` shortcut
 * * `MenuSub` / `DropdownMenuSub` — submenu root
 * * `MenuSubTrigger` / `DropdownMenuSubTrigger` — submenu trigger; `inset?: boolean`
 * * `MenuSubPopup` / `DropdownMenuSubContent` — submenu popup
 * * `MenuCreateHandle` / `DropdownMenuCreateHandle` — `MenuPrimitive.createHandle` re-export
 *
 * ## Shadcn UI
 * Dropdown only (no ctx menu). No `switch` checkbox variant. `MenuItem` variant is `'destructive'`. `MenuShortcut` is a `<span>`.
 * * `DropdownMenu` — root
 * * `DropdownMenuPortal` — portal
 * * `DropdownMenuTrigger` — trigger
 * * `DropdownMenuContent` — collapses Portal + Content; `sideOffset`
 * * `DropdownMenuGroup` — group
 * * `DropdownMenuItem` — item; `inset?: boolean`, `variant?: 'default'|'destructive'`
 * * `DropdownMenuCheckboxItem` — checkbox item
 * * `DropdownMenuRadioGroup` — radio group
 * * `DropdownMenuRadioItem` — radio item
 * * `DropdownMenuLabel` — label; `inset?: boolean`
 * * `DropdownMenuSeparator` — separator
 * * `DropdownMenuShortcut` — `<span>` shortcut
 * * `DropdownMenuSub` — submenu root
 * * `DropdownMenuSubTrigger` — submenu trigger; `inset?: boolean`
 * * `DropdownMenuSubContent` — submenu content
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */

'use client'

import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import type * as React from 'react'
import { CheckIcon, ChevronRightIcon, CircleIcon } from './icon'
import { Switch } from './switch'
import { cn } from '../lib/utils'

const MenuCreateHandle = MenuPrimitive.createHandle
const Menu = MenuPrimitive.Root
const MenuPortal = MenuPrimitive.Portal

function MenuTrigger(props: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="menu-trigger" {...props} />
}

function MenuPopup({
  children,
  className,
  sideOffset = 4,
  align = 'center',
  alignOffset,
  side = 'bottom',
  small,
  ...props
}: MenuPrimitive.Popup.Props & {
  align?: MenuPrimitive.Positioner.Props['align']
  sideOffset?: MenuPrimitive.Positioner.Props['sideOffset']
  alignOffset?: MenuPrimitive.Positioner.Props['alignOffset']
  side?: MenuPrimitive.Positioner.Props['side']
  /** Dense rows: tighter item padding, smaller radius/text. Items read it via `in-data-[small]`. */
  small?: boolean
}) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="z-50"
        data-slot="menu-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          className={cn(
            "bg-surface-3 relative flex origin-(--transform-origin) rounded-[12px] outline-none not-dark:bg-clip-padding not-[class*='w-']:min-w-32 focus:outline-none",
            className,
          )}
          data-slot="menu-popup"
          data-small={small ? '' : undefined}
          {...props}
        >
          <div className="max-h-(--available-height) w-full overflow-y-auto p-1">
            {children}
          </div>
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function MenuGroup(props: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="menu-group" {...props} />
}

function MenuItem({
  className,
  inset,
  variant = 'default',
  children,
  ...props
}: MenuPrimitive.Item.Props & {
  inset?: boolean
  variant?: 'default' | 'error'
}) {
  const hasLabel = [children].flat().some((child) => typeof child === 'string')

  return (
    <MenuPrimitive.Item
      className={cn(
        "data-highlighted:bg-primary/8 data-[variant=error]:text-error data-highlighted:text-on-surface text-label-medium flex min-h-8 cursor-pointer items-center gap-2 rounded-md p-3 whitespace-nowrap outline-none select-none data-disabled:cursor-not-allowed! data-disabled:opacity-64 data-inset:ps-8 [&>svg]:pointer-events-none [&>svg]:-mx-0.5 [&>svg]:shrink-0 [&>svg:not([class*='opacity-'])]:opacity-80 [&>svg:not([class*='size-'])]:size-5",
        "in-data-[small]:text-label-small in-data-[small]:min-h-6 in-data-[small]:gap-1.5 in-data-[small]:[&>svg:not([class*='size-'])]:size-4",
        hasLabel
          ? 'in-data-[slot=menu-row]:flex-1'
          : 'in-data-[slot=menu-row]:justify-center',
        className,
      )}
      data-inset={inset}
      data-slot="menu-item"
      data-variant={variant}
      {...props}
    >
      {children}
    </MenuPrimitive.Item>
  )
}

function MenuRow({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-row justify-center gap-0.5', className)}
      data-slot="menu-row"
      {...props}
    />
  )
}

function MenuCheckboxItem({
  className,
  children,
  checked,
  variant = 'default',
  ...props
}: MenuPrimitive.CheckboxItem.Props & {
  variant?: 'default' | 'switch'
}) {
  return (
    <MenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(
        "data-highlighted:bg-primary/8 data-highlighted:text-on-surface text-label-medium grid min-h-8 cursor-pointer items-center gap-2 rounded-md p-3 outline-none in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] data-disabled:cursor-not-allowed! data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        "in-data-[small]:text-label-small in-data-[small]:min-h-6 in-data-[small]:[&_svg:not([class*='size-'])]:size-4",
        'grid-cols-[1fr_auto] gap-4',
        className,
      )}
      data-slot="menu-checkbox-item"
      {...props}
    >
      <span className="col-start-1 flex items-center gap-2 select-none">
        {children}
      </span>

      {variant === 'switch' ? (
        <Switch
          checked={checked}
          className="pointer-events-none"
          scale={3}
          tabIndex={-1}
        />
      ) : (
        <MenuPrimitive.CheckboxItemIndicator className="col-start-2">
          <CheckIcon />
        </MenuPrimitive.CheckboxItemIndicator>
      )}
    </MenuPrimitive.CheckboxItem>
  )
}

function MenuRadioGroup(props: MenuPrimitive.RadioGroup.Props) {
  return <MenuPrimitive.RadioGroup data-slot="menu-radio-group" {...props} />
}

function MenuRadioItem({
  className,
  children,
  ...props
}: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      className={cn(
        "data-highlighted:bg-primary/8 data-highlighted:text-on-surface text-label-medium grid min-h-8 cursor-pointer grid-cols-[1fr_auto] items-center gap-4 rounded-md p-3 outline-none in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] data-disabled:cursor-not-allowed! data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        "in-data-[small]:text-label-small in-data-[small]:min-h-6 in-data-[small]:[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="menu-radio-item"
      closeOnClick
      {...props}
    >
      <span className="col-start-1 flex items-center gap-2">{children}</span>
      <MenuPrimitive.RadioItemIndicator className="col-start-2">
        <CircleIcon className="size-2 fill-current" />
      </MenuPrimitive.RadioItemIndicator>
    </MenuPrimitive.RadioItem>
  )
}

function MenuGroupLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean
}) {
  return (
    <MenuPrimitive.GroupLabel
      className={cn(
        'text-on-surface-secondary text-label-small cursor-default px-2 py-1.5 text-center font-medium select-none data-inset:ps-9 sm:data-inset:ps-8',
        className,
      )}
      data-inset={inset}
      data-slot="menu-label"
      {...props}
    />
  )
}

function MenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      className={cn('bg-separator mx-2 my-1 h-px rounded-full', className)}
      data-slot="menu-separator"
      {...props}
    />
  )
}

// FIX: replace with the Text.
function MenuShortcut({ className, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn(
        'text-on-surface-secondary text-label-small ms-auto ps-6 font-sans font-medium tracking-widest',
        className,
      )}
      data-slot="menu-shortcut"
      {...props}
    />
  )
}

function MenuSub(props: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="menu-sub" {...props} />
}

function MenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      className={cn(
        "data-popup-open:bg-primary/8 data-highlighted:bg-primary/8 data-highlighted:text-on-surface data-popup-open:text-on-surface text-label-medium flex min-h-8 cursor-pointer items-center gap-2 rounded-md p-3 whitespace-nowrap outline-none data-disabled:cursor-not-allowed! data-disabled:opacity-64 data-inset:ps-8 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5",
        "in-data-[small]:text-label-small in-data-[small]:min-h-6 in-data-[small]:[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-inset={inset}
      data-slot="menu-sub-trigger"
      {...props}
    >
      {children}
      <ChevronRightIcon className="ms-auto -me-0.5 opacity-80" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

// SUS: I think it should appear inside (like in the old repo), not outside.
function MenuSubPopup({
  className,
  sideOffset = 0,
  alignOffset,
  align = 'start',
  ...props
}: MenuPrimitive.Popup.Props & {
  align?: MenuPrimitive.Positioner.Props['align']
  sideOffset?: MenuPrimitive.Positioner.Props['sideOffset']
  alignOffset?: MenuPrimitive.Positioner.Props['alignOffset']
}) {
  const defaultAlignOffset = align === 'center' ? undefined : -5

  return (
    <MenuPopup
      align={align}
      alignOffset={alignOffset ?? defaultAlignOffset}
      className={className}
      data-slot="menu-sub-content"
      side="inline-end"
      sideOffset={sideOffset}
      {...props}
    />
  )
}

// INFO: exports duplicated with renaming because (Context) Menu and Dropdown Menu are sort of the same component.
export {
  MenuRow,
  MenuCreateHandle,
  MenuCreateHandle as DropdownMenuCreateHandle,
  Menu,
  Menu as DropdownMenu,
  MenuPortal,
  MenuPortal as DropdownMenuPortal,
  MenuTrigger,
  MenuTrigger as DropdownMenuTrigger,
  MenuPopup,
  MenuPopup as DropdownMenuContent,
  MenuGroup,
  MenuGroup as DropdownMenuGroup,
  MenuItem,
  MenuItem as DropdownMenuItem,
  MenuCheckboxItem,
  MenuCheckboxItem as DropdownMenuCheckboxItem,
  MenuRadioGroup,
  MenuRadioGroup as DropdownMenuRadioGroup,
  MenuRadioItem,
  MenuRadioItem as DropdownMenuRadioItem,
  MenuGroupLabel,
  MenuGroupLabel as DropdownMenuLabel,
  MenuSeparator,
  MenuSeparator as DropdownMenuSeparator,
  MenuShortcut,
  MenuShortcut as DropdownMenuShortcut,
  MenuSub,
  MenuSub as DropdownMenuSub,
  MenuSubTrigger,
  MenuSubTrigger as DropdownMenuSubTrigger,
  MenuSubPopup,
  MenuSubPopup as DropdownMenuSubContent,
}
